import React from "react";
import { API } from "@/lib/api";

/**
 * Tier ring → matches the warning severity colour scheme used in
 * WarningsAdmin (hard=red, soft=orange) plus a positive green ring for users
 * who have appreciations and no active warnings. Deactivated users keep a
 * gray override so they're clearly distinct from "warned but active".
 */
const TIER_RING = {
  hard: "ring-2 ring-red-500 ring-offset-2 ring-offset-white",
  soft: "ring-2 ring-orange-400 ring-offset-2 ring-offset-white",
  appreciation: "ring-2 ring-emerald-500 ring-offset-2 ring-offset-white",
  none: "",
};

const TIER_LABEL = {
  hard: "Hard warning active",
  soft: "Soft warning active",
  appreciation: "Recent appreciation",
  none: "",
};

function initials(name) {
  if (!name) return "U";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || name[0].toUpperCase();
}

/**
 * Unified user avatar — tier-coloured ring based on user.conduct_tier.
 * Use this everywhere a staff photo appears.
 *
 * Props:
 *   user: { avatar_path?, name?, active?, conduct_tier? }
 *   size: pixel size (default 40)
 *   showRing: false to disable the ring for places that should stay neutral
 */
export default function UserAvatar({ user, size = 40, showRing = true, className = "" }) {
  const tier = user?.conduct_tier || "none";
  const isInactive = user?.active === false;
  const ring = showRing && !isInactive ? (TIER_RING[tier] || "") : "";
  const grayscale = isInactive ? "grayscale opacity-60" : "";
  const title = !isInactive && TIER_LABEL[tier] ? TIER_LABEL[tier] : (user?.name || "");
  const url = user?.avatar_path ? `${API}/files?path=${encodeURIComponent(user.avatar_path)}` : null;
  const style = { width: size, height: size, fontSize: Math.round(size * 0.36) };

  if (url) {
    return (
      <img
        src={url}
        alt={user?.name || "Staff"}
        title={title}
        className={`rounded-full object-cover border border-zinc-200 ${ring} ${grayscale} ${className}`}
        style={style}
        loading="lazy"
        data-conduct-tier={tier}
      />
    );
  }
  return (
    <div
      className={`rounded-full bg-zinc-100 text-zinc-600 flex items-center justify-center font-semibold border border-zinc-200 ${ring} ${grayscale} ${className}`}
      style={style}
      title={title}
      data-conduct-tier={tier}
    >
      {initials(user?.name)}
    </div>
  );
}

/** Tier metadata for tints / pills used in row backgrounds, banners, etc. */
export const TIER_TINTS = {
  hard: { row: "border-l-4 border-red-500 bg-red-50/40", pill: "bg-red-100 text-red-800 border-red-200", label: "Hard warning" },
  soft: { row: "border-l-4 border-orange-400 bg-orange-50/40", pill: "bg-orange-100 text-orange-800 border-orange-200", label: "Soft warning" },
  appreciation: { row: "border-l-4 border-emerald-500 bg-emerald-50/40", pill: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Appreciated" },
  none: { row: "", pill: "", label: "" },
};
